import { useContext, useState } from "react";
import Loading from "../Generic/loading";
import { formatShortDuration, parseEmsDate } from "../Logic/emsDates";
import { tenantContext } from "../Main/contexts";
import { attentionLines } from "./attentionLines";
import AttentionStrip from "./components/AttentionStrip";
import SummaryBand from "./components/SummaryBand";
import TenantDetail, { EmailTarget } from "./components/TenantDetail";
import TenantTable from "./components/TenantTable";
import TrendBand from "./components/TrendBand";
import { kpiCounts } from "./derive";
import "./styles.css";
import { AggregateSeries, aggregateSeries } from "./trendSeries";
import { useTenantOverview } from "./useTenantOverview";
import { useTenantTrends } from "./useTenantTrends";

// Backlog rising-run tracking (a separate concern from the throughput/backlog
// history this file now fans out for) is still a later task's work — Spec 2
// replaces this. A module constant rather than a per-render Map is
// deliberate: it needs no stable-identity trick (useMemo(() => new Map(), []))
// to keep AttentionStrip and TenantTable's props referentially stable,
// because it never changes — a module constant reads as an invariant, where a
// memo reads as "could change but currently doesn't".
const NO_BEHIND_RUNS: Map<string, number> = new Map();

/** Stable placeholder so the not-ready branch does not allocate per render. */
const EMPTY_AGGREGATE: AggregateSeries = { points: [], minTenants: 0, maxTenants: 0, total: 0 };

/**
 * Which tenant's detail view is open, if any.
 *
 * Carries the display name as well as the key so `TenantDetail`'s header can
 * render before its own request resolves — and because the roster row it came
 * from is not guaranteed to still be in `rows` after the next overview poll.
 */
interface Inspecting {
    tenantKey: string;
    name: string;
}

/**
 * Deep-link target for the tenant email worklist: a tenant, optionally
 * narrowed to one of its mailboxes.
 *
 * Defined in `TenantDetail` (see there) — the one place that actually
 * constructs a value of this shape — and re-exported here so `Main`, which
 * already imports this module for the component itself, has one place to get
 * it from.
 */
export type { EmailTarget };

/**
 * Props for {@link CoreMailProcessing}.
 *
 * `onOpenEmails` is threaded straight through to `TenantDetail`, which is the
 * only place in this file with a control that fires it — see the "View
 * emails" button there. `Main` owns navigation and supplies the callback;
 * this component and `TenantDetail` own none of it.
 */
export interface CoreMailProcessingProps {
    onOpenEmails?: (target: EmailTarget) => void;
}

/**
 * Cross-tenant mail-processing triage view.
 *
 * These two Core routes ignore the tenant claim in the token and read across
 * every tenant, so any sys-admin token works here. The token is taken from the
 * app-wide selected tenant purely because that is where a valid one lives —
 * this page is not scoped to it.
 *
 * Phase 2: trend band and per-tenant sparklines are wired to the real history
 * fan-out (`useTenantTrends`). Backlog direction chips still receive an empty
 * map — that is Spec 2's work, not this one's.
 *
 * Spec 3A: a tenant name opens `TenantDetail`, which mints its own scoped
 * token. The app-wide `selectedTenant` is deliberately untouched — see
 * useTenantInspection.
 */
function CoreMailProcessing({ onOpenEmails }: CoreMailProcessingProps = {}) {
    const { selectedTenant } = useContext(tenantContext);
    const { state, overview, reload } = useTenantOverview(selectedTenant?.token);

    // `reload` only flips the hook's state when its response resolves — a
    // click during an outage would otherwise re-set "failed" while already
    // "failed", which produces no re-render and no visible sign the click
    // registered. This local flag drives the button's own feedback instead.
    const [retrying, setRetrying] = useState(false);

    // Owned here rather than inside TenantDetail because it is what selects
    // between the two views. TenantDetail takes its tenant as props and owns
    // no navigation state.
    const [inspecting, setInspecting] = useState<Inspecting | undefined>(undefined);

    // `tenants` is optional on TenantOverview (contract: every aggregate is
    // absent-not-null, and the DTO comment applies the same rule to this
    // field). This is NOT the "absent is not zero" defect the numeric
    // aggregates guard against: there is no separate "zero tenants" state
    // this page could show that differs from "no roster came back". Either
    // way TenantTable's own empty state — "check the collector configuration
    // rather than this page" — is the correct and only message, so collapsing
    // an absent list into an empty one loses no information a reader could
    // act on.
    const rows = overview?.tenants ?? [];

    const trends = useTenantTrends(selectedTenant?.token, rows, overview?.asOf);

    /*
     * The triage subtree unmounts while a tenant is open, and that is safe:
     * `useTenantOverview` and `useTenantTrends` are both called HERE, in a
     * component that stays mounted, so neither refetches on the way back.
     * `TenantTable` holds no state of its own, so nothing but the table's
     * scroll position is lost.
     *
     * If either hook is ever moved down into a child component this stops
     * being true and every round trip costs 40 history requests.
     * index.test.tsx has a test pinned to exactly that.
     *
     * Placed here, right after the hooks and before the derivations below,
     * so none of those derivations — aggregate, counts, attention lines —
     * run at all while a tenant is open. TenantDetail needs only the key,
     * the name, and a way back; nothing below this point.
     */
    if (inspecting) {
        return (
            <TenantDetail
                tenantKey={inspecting.tenantKey}
                name={inspecting.name}
                onBack={() => setInspecting(undefined)}
                onOpenEmails={onOpenEmails}
            />
        );
    }

    // Not memoised. Renders ARE frequent for a window — once per progressive
    // fan-out completion, up to forty times, while history is loading — but
    // the "ready" gate below is what actually protects that window, not a
    // memo: while the fan-out is landing, the ternary takes the EMPTY_AGGREGATE
    // branch and `aggregateSeries` is never called at all, so there is nothing
    // for a memo to skip. Without the gate, it WOULD run on all forty updates
    // for data `TrendBand` never shows, since it short-circuits to its loading
    // note during that window. After the fan-out settles, renders are
    // genuinely infrequent (only the five-minute poll and a retry flag), and
    // the per-render cost of a fresh `aggregate` identity reaching the charts
    // is answered by memoising the CHARTS, not this line. Revisit if
    // page-level frequently-changing state is ever added outside the load
    // window. (And it does not run at all while a tenant is open — the
    // early return above sends that render down a different path entirely.)
    const aggregate =
        trends.state === "ready" ? aggregateSeries(Array.from(trends.series.values()), rows.length) : EMPTY_AGGREGATE;

    const counts = kpiCounts(rows, undefined);
    const lines = attentionLines(rows, NO_BEHIND_RUNS);

    const asOf = parseEmsDate(overview?.asOf);
    const asOfClock = asOf?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const asOfAge = asOf ? formatShortDuration((Date.now() - asOf.getTime()) / 1000) : undefined;

    async function handleRetry() {
        setRetrying(true);
        try {
            // The overview and the history store are different backends —
            // but Retry is only reachable when the OVERVIEW has failed
            // (`state === "failed"` gates the button below), and the
            // dominant case under that gate is a healthy trend band next to
            // a broken overview. Retrying trends unconditionally would blank
            // 40 working sparklines and the chart band on every such click,
            // for a fetch that was never broken — exactly the "blank still-
            // useful data on a failed poll" defect phase 1 already guards
            // against for the overview itself. So only retry trends if
            // trends are themselves broken, and decide that BEFORE the
            // overview's reload runs, so nothing it does can change the
            // answer out from under this click.
            const trendsNeedRetry = trends.state === "failed" || trends.failedKeys.size > 0;
            await reload();
            if (trendsNeedRetry) {
                trends.reload();
            }
        } finally {
            setRetrying(false);
        }
    }

    return (
        <>
            <div className="topSection">
                <div className="sectionHeader">Mail Processing — All Tenants</div>
                <div className="mp-asof">{asOfClock && `as of ${asOfClock}`}</div>
            </div>

            {/*
                Not reachable through the app today: Main.tsx gates this page's whole nav
                entry on selectedTenant already being set, so `state` never actually
                starts "idle" here in practice. Kept anyway as defence against that gate
                changing or this component being mounted some other way — a nav change
                elsewhere should not leave this page spinning forever with nothing to
                diagnose from. Covered by index.test.tsx; not dead code.
            */}
            {state === "idle" && (
                <div className="mp-notice">
                    Select a tenant to load the overview. This page reads across all tenants; the selection only
                    supplies a valid sys-admin token.
                </div>
            )}

            {state === "loading" && <Loading></Loading>}

            {state === "forbidden" && (
                <div className="mp-notice">
                    This page requires sys-admin. Cross-tenant mail processing is support tooling, not end-user
                    functionality.
                </div>
            )}

            {state === "unauthorised" && (
                <div className="mp-notice">Your session has expired. Reload the page to sign in again.</div>
            )}

            {state === "failed" && (
                <div className="mp-notice">
                    <p>The overview request failed.</p>
                    {overview && (
                        <p className="mp-notice-detail">
                            Showing the last figures received
                            {asOfClock && `, from ${asOfClock}${asOfAge ? ` (${asOfAge} ago)` : ""}`}.
                        </p>
                    )}
                    <button
                        className="g-btn btn-default"
                        onClick={() => void handleRetry()}
                        disabled={retrying}
                    >
                        {retrying ? "Retrying…" : "Retry"}
                    </button>
                </div>
            )}

            {overview && (state === "loaded" || state === "failed") && (
                <>
                    <SummaryBand counts={counts} />
                    <TrendBand
                        data={aggregate}
                        state={trends.state}
                    />
                    <AttentionStrip lines={lines} />
                    {/*
                        `trends.series` and `trends.failedKeys` are passed through
                        unchanged, including while `trends.state === "loading"`. A
                        tenant not yet in `trends.series` during the fan-out falls
                        through to Sparkline's own "no data" branch, whose title
                        ("No throughput measured...") is factually about the
                        terminal empty-window case, not "not fetched yet" — a
                        known small inaccuracy for the ~30-second initial load.
                        Fixing it means changing Sparkline's copy or adding a
                        distinct hydrating signal, both out of this file's scope;
                        TenantTable's own "no data" and trendFailures' "history could not be read"
                        remain honestly distinct from each other, which is the
                        distinction this table is actually responsible for.
                    */}
                    {/*
                        Not wrapped in React.memo. `trends.series` gets a fresh
                        Map identity on every one of the ~40 progressive fan-out
                        completions (by design — see useTenantTrends), so a
                        shallow-prop-comparing memo on TenantTable itself would
                        never skip a render: the prop that changes every time is
                        exactly the one being watched. `trends.failedKeys` has the
                        identical problem for the identical reason — a fresh Set
                        on every per-tenant failure. The fix that would work — a
                        per-row memo keyed on that tenant's own TrendSeries
                        reference, plus that tenant's own failed/not-failed
                        boolean rather than the raw Set — needs a row extracted
                        inside TenantTable.tsx, which is outside this file's
                        scope. Left for a follow-up rather than done silently or
                        half-done here.
                    */}
                    <TenantTable
                        rows={rows}
                        behindRuns={NO_BEHIND_RUNS}
                        trends={trends.series}
                        trendFailures={trends.failedKeys}
                        onInspect={(tenantKey, name) => setInspecting({ tenantKey, name })}
                    />
                </>
            )}
        </>
    );
}

export default CoreMailProcessing;
